import { useEffect, useRef, type RefObject } from 'react';

/**
 * Calls `onDismiss` on Escape, and on a pointer press outside `ref` when given. The latest `onDismiss` is read
 * through a ref, so callers can pass an inline function without the listeners being re-attached on every render.
 */
export function useDismiss(active: boolean, onDismiss: () => void, ref?: RefObject<HTMLElement | null>) {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!active) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismissRef.current();
    };
    const onPointer = (e: PointerEvent) => {
      if (ref?.current && !ref.current.contains(e.target as Node)) onDismissRef.current();
    };

    document.addEventListener('keydown', onKey);
    if (ref) document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (ref) document.removeEventListener('pointerdown', onPointer);
    };
  }, [active, ref]);
}
